<template>
  <div class="billing-page">
    <header class="page-header">
      <h1 class="page-title">Billing &amp; subscription</h1>
      <p class="page-subtitle">
        Manage your Praetorian AI plan, monthly token quota, and invoice history for this organization.
      </p>
    </header>

    <div v-if="loading" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i>
      Loading billing information…
    </div>

    <template v-else>
      <!-- Current subscription -->
      <section
        v-if="subscriptionStatus.has_subscription"
        class="settings-card edge-card"
        :class="subscriptionEdgeClass"
      >
        <div class="card-header">
          <i class="fas fa-star" aria-hidden="true"></i>
          <div>
            <h2 class="card-title">Current subscription</h2>
            <p class="card-desc">
              {{ subscription.plan }} plan · {{ formatStatus(subscription.status) }}
              <span v-if="subscription.cancel_at_period_end"> · cancels at period end</span>
            </p>
          </div>
        </div>

        <div class="card-body">
          <dl class="metrics-list">
            <div class="metric-row">
              <dt>Billing cycle</dt>
              <dd>{{ subscription.interval === 'MONTH' ? 'Monthly' : 'Annual' }}</dd>
            </div>
            <div class="metric-row">
              <dt>Amount</dt>
              <dd>${{ subscription.base_price }}/{{ subscription.interval === 'MONTH' ? 'month' : 'year' }}</dd>
            </div>
            <div class="metric-row">
              <dt>Current period</dt>
              <dd>{{ formatDate(subscription.current_period_start) }} – {{ formatDate(subscription.current_period_end) }}</dd>
            </div>
            <div v-if="subscriptionStatus.is_trialing" class="metric-row metric-row--warn">
              <dt>Trial ends</dt>
              <dd>{{ formatDate(subscriptionStatus.trial_end) }}</dd>
            </div>
            <div v-if="subscription.cancel_at_period_end" class="metric-row metric-row--danger">
              <dt>Cancels on</dt>
              <dd>{{ formatDate(subscription.current_period_end) }}</dd>
            </div>
          </dl>

          <div v-if="subscription.features?.length" class="features-block">
            <h3 class="features-title">Plan features</h3>
            <ul class="feature-checklist">
              <li v-for="(feature, index) in subscription.features" :key="index">
                <i class="fas fa-check" aria-hidden="true"></i>
                {{ feature }}
              </li>
            </ul>
          </div>

          <div class="card-actions">
            <button
              class="btn btn-primary"
              :disabled="portalLoading"
              @click="openCustomerPortal"
            >
              <i class="fas fa-external-link-alt"></i>
              {{ portalLoading ? 'Opening…' : 'Manage in Stripe' }}
            </button>
            <button
              v-if="!subscription.cancel_at_period_end"
              class="btn btn-secondary btn-danger-outline"
              @click="showCancelConfirm = true"
            >
              Cancel subscription
            </button>
            <button
              v-else
              class="btn btn-secondary"
              :disabled="reactivating"
              @click="reactivateSubscription"
            >
              <i class="fas fa-undo"></i>
              {{ reactivating ? 'Reactivating…' : 'Reactivate subscription' }}
            </button>
          </div>
        </div>
      </section>

      <!-- No subscription -->
      <section v-else class="settings-card edge-card edge-neutral">
        <div class="card-header">
          <i class="fas fa-inbox" aria-hidden="true"></i>
          <div>
            <h2 class="card-title">No active subscription</h2>
            <p class="card-desc">
              Choose a plan to unlock full AI analysis, higher token limits, and premium compliance features.
            </p>
          </div>
        </div>
        <div class="card-body card-body--center">
          <button class="btn btn-primary" @click="showPricingPlans = true">
            <i class="fas fa-layer-group"></i>
            View pricing plans
          </button>
        </div>
      </section>

      <!-- Token usage -->
      <section class="settings-card">
        <div class="card-header card-header--split">
          <div class="card-header-main">
            <i class="fas fa-robot" aria-hidden="true"></i>
            <div>
              <h2 class="card-title">AI token usage</h2>
              <p class="card-desc">
                Monthly quota for LLM-powered analysis, consult agents, and document review.
              </p>
            </div>
          </div>
          <span v-if="tokenUsage.usage?.billing_period_start" class="period-chip">
            {{ formatDate(tokenUsage.usage.billing_period_start) }} period
          </span>
        </div>

        <div class="card-body">
          <div v-if="tokenUsageLoading" class="inline-loading">
            <i class="fas fa-spinner fa-spin"></i>
            Loading usage data…
          </div>

          <div v-else-if="tokenUsage.usage" class="token-usage-content">
            <div class="usage-meter">
              <div class="usage-labels">
                <span class="used">{{ tokenUsage.usage.display?.used_formatted || formatNumber(tokenUsage.usage.used) }} used</span>
                <span class="limit">{{ tokenUsage.usage.display?.limit_formatted || formatNumber(tokenUsage.usage.limit) }} limit</span>
              </div>
              <div class="progress-bar">
                <div
                  class="progress-fill"
                  :class="getUsageColorClass(tokenUsage.usage.percentage)"
                  :style="{ width: Math.min(tokenUsage.usage.percentage, 100) + '%' }"
                ></div>
                <div
                  v-if="tokenUsage.usage.percentage > 100"
                  class="progress-overage"
                  :style="{ width: Math.min(tokenUsage.usage.percentage - 100, 100) + '%' }"
                ></div>
              </div>
              <div class="usage-stats">
                <span class="percentage" :class="getUsageColorClass(tokenUsage.usage.percentage)">
                  {{ tokenUsage.usage.display?.percentage_formatted || tokenUsage.usage.percentage.toFixed(1) + '%' }}
                </span>
                <span class="remaining">{{ formatNumber(tokenUsage.usage.remaining) }} remaining</span>
              </div>
            </div>

            <div
              v-if="tokenUsage.usage.warning_level !== 'ok'"
              class="usage-alert"
              :class="tokenUsage.usage.warning_level"
            >
              <i :class="getWarningIcon(tokenUsage.usage.warning_level)"></i>
              <span v-if="tokenUsage.usage.warning_level === 'warning'">
                You've used {{ tokenUsage.usage.percentage.toFixed(1) }}% of your monthly token quota.
              </span>
              <span v-else-if="tokenUsage.usage.warning_level === 'critical'">
                Critical: only {{ formatNumber(tokenUsage.usage.remaining) }} tokens remaining this period.
              </span>
              <span v-else-if="tokenUsage.usage.warning_level === 'exceeded'">
                Quota exceeded.
                <span v-if="!tokenUsage.usage.is_hard_limit">
                  Overage: {{ formatNumber(tokenUsage.usage.overage_tokens) }} tokens
                  (est. ${{ tokenUsage.usage.overage_cost?.toFixed(2) || '0.00' }}).
                </span>
                <span v-else>AI features are temporarily disabled.</span>
              </span>
            </div>

            <dl class="usage-metrics">
              <div class="metric-row">
                <dt>Cost to date</dt>
                <dd>${{ tokenUsage.usage.cost_to_date?.toFixed(2) || '0.00' }}</dd>
              </div>
              <div v-if="tokenUsage.usage.overage_cost > 0" class="metric-row metric-row--danger">
                <dt>Overage cost</dt>
                <dd>${{ tokenUsage.usage.overage_cost?.toFixed(2) || '0.00' }}</dd>
              </div>
              <div class="metric-row">
                <dt>Overage rate</dt>
                <dd>${{ tokenUsage.usage.overage_rate?.toFixed(2) || '5.00' }} / million tokens</dd>
              </div>
            </dl>

            <div v-if="tokenUsage.usage.percentage >= 80" class="card-actions card-actions--compact">
              <button class="btn btn-primary btn-sm" @click="showPricingPlans = true">
                <i class="fas fa-arrow-up"></i>
                Upgrade plan
              </button>
            </div>
          </div>

          <div v-else class="empty-state">
            <i class="fas fa-chart-line"></i>
            <p>Token usage will appear here once you start using AI features.</p>
          </div>
        </div>
      </section>

      <!-- Invoices -->
      <section class="settings-card">
        <div class="card-header">
          <i class="fas fa-file-invoice" aria-hidden="true"></i>
          <div>
            <h2 class="card-title">Invoices</h2>
            <p class="card-desc">Recent billing history from Stripe for this organization.</p>
          </div>
        </div>

        <div class="card-body">
          <div v-if="invoices.length === 0" class="empty-state empty-state--compact">
            <p>No invoices yet.</p>
          </div>
          <div v-else class="table-wrap">
            <table class="invoices-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="invoice in invoices" :key="invoice.id">
                  <td>{{ invoice.invoice_number || `#${invoice.id}` }}</td>
                  <td>{{ formatDate(invoice.created_at) }}</td>
                  <td class="amount">${{ invoice.total }}</td>
                  <td>
                    <span class="status-chip" :class="'is-' + invoice.status.toLowerCase()">
                      {{ invoice.status }}
                    </span>
                  </td>
                  <td class="row-actions">
                    <a
                      v-if="invoice.invoice_pdf_url"
                      :href="invoice.invoice_pdf_url"
                      target="_blank"
                      rel="noopener"
                      class="btn btn-sm btn-link"
                    >
                      PDF
                    </a>
                    <a
                      v-if="invoice.hosted_invoice_url"
                      :href="invoice.hosted_invoice_url"
                      target="_blank"
                      rel="noopener"
                      class="btn btn-sm btn-link"
                    >
                      View
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </template>

    <PricingPlansModal
      v-if="showPricingPlans"
      @close="showPricingPlans = false"
      @select="handlePlanSelect"
    />

    <div v-if="showCancelConfirm" class="modal-overlay" @click.self="showCancelConfirm = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Cancel subscription?</h3>
          <button class="modal-close" @click="showCancelConfirm = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to cancel your subscription?</p>
          <p class="text-muted">
            Your subscription stays active until {{ formatDate(subscription.current_period_end) }}.
            After that, premium features will be unavailable.
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showCancelConfirm = false">
            Keep subscription
          </button>
          <button class="btn btn-danger" :disabled="canceling" @click="confirmCancel">
            {{ canceling ? 'Canceling…' : 'Yes, cancel' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import PricingPlansModal from '@/components/billing/PricingPlansModal.vue';

export default {
  name: 'BillingSettings',
  
  components: {
    PricingPlansModal,
  },

  data() {
    return {
      loading: true,
      subscriptionStatus: {
        has_subscription: false,
      },
      subscription: null,
      invoices: [],
      showPricingPlans: false,
      showCancelConfirm: false,
      portalLoading: false,
      canceling: false,
      reactivating: false,
      // Token usage
      tokenUsage: {
        usage: null,
        can_use_ai: true,
      },
      tokenUsageLoading: false,
    };
  },

  async mounted() {
    await Promise.all([
      this.loadBillingData(),
      this.loadTokenUsage(),
    ]);

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      this.$toast?.success('Subscription activated successfully!');
      window.history.replaceState({}, '', this.billingPath);
    }
  },

  computed: {
    billingPath() {
      return this.$route.path.startsWith('/settings/') ? '/settings/billing' : '/billing';
    },
    subscriptionEdgeClass() {
      if (!this.subscription) return 'edge-ok';
      if (this.subscription.cancel_at_period_end) return 'edge-warn';
      if (this.subscription.status?.toLowerCase() === 'past_due') return 'edge-warn';
      if (this.subscriptionStatus.is_trialing) return 'edge-neutral';
      return 'edge-ok';
    },
  },

  methods: {
    async loadBillingData() {
      this.loading = true;
      try {
        // Load subscription status
        const statusResponse = await api.get('/users/billing/subscription_status/');
        this.subscriptionStatus = statusResponse.data;

        // Load full subscription details if active
        if (this.subscriptionStatus.has_subscription) {
          try {
            const subResponse = await api.get('/users/billing/current_subscription/');
            this.subscription = subResponse.data;
          } catch (error) {
            console.error('Error loading subscription details:', error);
          }
        }

        // Load invoices
        try {
          const invoicesResponse = await api.get('/users/billing/invoices/', {
            params: { limit: 12 }
          });
          this.invoices = invoicesResponse.data;
        } catch (error) {
          console.error('Error loading invoices:', error);
        }
      } catch (error) {
        console.error('Error loading billing data:', error);
        this.$toast?.error('Failed to load billing information');
      } finally {
        this.loading = false;
      }
    },

    async handlePlanSelect(plan, interval) {
      try {
        const currentUrl = window.location.origin;
        const response = await api.post('/users/billing/create_checkout_session/', {
          plan,
          interval,
          success_url: `${currentUrl}${this.billingPath}?success=true`,
          cancel_url: `${currentUrl}${this.billingPath}`,
        });

        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } catch (error) {
        console.error('Error creating checkout session:', error);
        
        const errorData = error.response?.data;
        
        if (errorData?.error_type === 'stripe_not_configured') {
          this.$toast?.error(
            '⚙️ Stripe Not Configured\n\n' +
            'Platform payment processing is not set up yet. ' +
            'The platform administrator needs to configure Stripe API keys.\n\n' +
            'Contact support for assistance.',
            { timeout: 8000 }
          );
        } else {
          this.$toast?.error(errorData?.error || 'Failed to start checkout process');
        }
      }
    },

    async openCustomerPortal() {
      this.portalLoading = true;
      try {
        const response = await api.post('/users/billing/customer_portal/', {
          return_url: window.location.href,
        });

        // Redirect to Stripe Customer Portal
        window.location.href = response.data.url;
      } catch (error) {
        console.error('Error opening customer portal:', error);
        this.$toast?.error('Failed to open billing portal');
        this.portalLoading = false;
      }
    },

    async confirmCancel() {
      this.canceling = true;
      try {
        await api.post('/users/billing/cancel_subscription/', {
          at_period_end: true,
        });

        this.$toast?.success('Subscription will be canceled at the end of the billing period');
        this.showCancelConfirm = false;
        await this.loadBillingData();
      } catch (error) {
        console.error('Error canceling subscription:', error);
        this.$toast?.error('Failed to cancel subscription');
      } finally {
        this.canceling = false;
      }
    },

    async reactivateSubscription() {
      this.reactivating = true;
      try {
        await api.post('/users/billing/reactivate_subscription/');
        
        this.$toast?.success('Subscription reactivated successfully!');
        await this.loadBillingData();
      } catch (error) {
        console.error('Error reactivating subscription:', error);
        this.$toast?.error('Failed to reactivate subscription');
      } finally {
        this.reactivating = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },

    formatStatus(status) {
      return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    // Token usage methods
    async loadTokenUsage() {
      this.tokenUsageLoading = true;
      try {
        const response = await api.get('/users/billing/token_usage/');
        this.tokenUsage = response.data;
      } catch (error) {
        console.error('Error loading token usage:', error);
        // Don't show error toast - this is a non-critical feature
      } finally {
        this.tokenUsageLoading = false;
      }
    },

    formatNumber(num) {
      if (num === null || num === undefined) return '0';
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
      }
      return num.toLocaleString();
    },

    getUsageColorClass(percentage) {
      if (percentage >= 100) return 'exceeded';
      if (percentage >= 95) return 'critical';
      if (percentage >= 80) return 'warning';
      return 'ok';
    },

    getWarningIcon(level) {
      switch (level) {
        case 'exceeded': return 'fas fa-exclamation-circle';
        case 'critical': return 'fas fa-exclamation-triangle';
        case 'warning': return 'fas fa-info-circle';
        default: return 'fas fa-check-circle';
      }
    },
  },
};
</script>

<style scoped>
.billing-page {
  max-width: 960px;
}

.page-header {
  margin-bottom: 1.5rem;
}

.page-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--p-ink, #1e293b);
  margin: 0 0 0.35rem;
}

.page-subtitle {
  margin: 0;
  font-size: 0.9rem;
  color: var(--p-ink-muted, #64748b);
  line-height: 1.5;
  max-width: 42rem;
}

.loading-state {
  padding: 2rem;
  text-align: center;
  color: var(--p-ink-muted, #64748b);
}

.loading-state i {
  margin-right: 0.45rem;
  color: var(--p-accent, #1e3a5f);
}

.settings-card {
  background: var(--p-surface-elevated, #fff);
  border: 1px solid var(--p-border, #e2e8f0);
  border-radius: var(--p-radius, 0.75rem);
  padding: 1.25rem 1.35rem;
  margin-bottom: 1.25rem;
}

.edge-card {
  border-left-width: 4px;
  padding-left: calc(1.35rem - 3px);
}

.edge-ok {
  border-left-color: #16a34a;
}

.edge-warn {
  border-left-color: #d97706;
}

.edge-neutral {
  border-left-color: var(--p-border-strong, #cbd5e1);
}

.card-header {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--p-border, #e2e8f0);
}

.card-header--split {
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}

.card-header-main {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
}

.card-header > i,
.card-header-main > i {
  color: var(--p-accent, #1e3a5f);
  font-size: 1.15rem;
  margin-top: 0.15rem;
  flex-shrink: 0;
}

.card-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--p-ink, #1e293b);
  margin: 0 0 0.25rem;
}

.card-desc {
  margin: 0;
  font-size: 0.85rem;
  color: var(--p-ink-muted, #64748b);
  line-height: 1.45;
}

.period-chip {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--p-ink-muted, #64748b);
  background: var(--p-surface-muted, #f8fafc);
  border: 1px solid var(--p-border, #e2e8f0);
  padding: 0.25rem 0.55rem;
  border-radius: 4px;
  align-self: flex-start;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.card-body--center {
  align-items: flex-start;
}

.metrics-list,
.usage-metrics {
  margin: 0;
  background: var(--p-surface-muted, #f8fafc);
  border: 1px solid var(--p-border, #e2e8f0);
  border-radius: var(--p-radius-sm, 0.5rem);
  padding: 0.35rem 0.85rem;
}

.metric-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--p-border, #e2e8f0);
}

.metric-row:last-child {
  border-bottom: none;
}

.metric-row dt {
  font-size: 0.8rem;
  color: var(--p-ink-muted, #64748b);
  font-weight: 500;
}

.metric-row dd {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--p-ink, #1e293b);
  text-align: right;
}

.metric-row--warn dd {
  color: #92400e;
}

.metric-row--danger dd {
  color: var(--p-danger, #ef4444);
}

.features-block {
  margin-top: 0.25rem;
}

.features-title {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--p-ink-muted, #64748b);
  margin: 0 0 0.65rem;
}

.feature-checklist {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.35rem 1rem;
}

.feature-checklist li {
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  font-size: 0.82rem;
  color: var(--p-ink-muted, #64748b);
}

.feature-checklist i {
  color: var(--p-success, #16a34a);
  margin-top: 0.15rem;
  font-size: 0.75rem;
}

.card-actions {
  display: flex;
  gap: 0.65rem;
  flex-wrap: wrap;
  padding-top: 0.85rem;
  border-top: 1px solid var(--p-border, #e2e8f0);
}

.card-actions--compact {
  border-top: none;
  padding-top: 0;
  justify-content: flex-end;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.9rem;
  border-radius: var(--p-radius-sm, 0.5rem);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  text-decoration: none;
  transition: background var(--p-transition, 180ms ease), border-color var(--p-transition, 180ms ease);
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--p-accent, #1e3a5f);
  color: var(--p-accent-contrast, #fff);
  border-color: var(--p-accent, #1e3a5f);
}

.btn-primary:hover:not(:disabled) {
  background: var(--p-accent-hover, #162b46);
}

.btn-secondary {
  background: var(--p-surface-elevated, #fff);
  color: var(--p-ink, #1e293b);
  border-color: var(--p-border-strong, #cbd5e1);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--p-accent-subtle, #eef2f8);
}

.btn-danger-outline {
  color: var(--p-danger, #ef4444);
  border-color: color-mix(in srgb, #ef4444 35%, #cbd5e1);
}

.btn-danger-outline:hover:not(:disabled) {
  background: #fef2f2;
  border-color: var(--p-danger, #ef4444);
}

.btn-danger {
  background: var(--p-danger, #ef4444);
  color: #fff;
  border-color: var(--p-danger, #ef4444);
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
}

.btn-sm {
  padding: 0.35rem 0.65rem;
  font-size: 0.8rem;
}

.btn-link {
  background: transparent;
  color: var(--p-accent, #1e3a5f);
  padding: 0.25rem 0.45rem;
  border: none;
}

.btn-link:hover {
  background: var(--p-accent-subtle, #eef2f8);
}

.inline-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--p-ink-muted, #64748b);
  font-size: 0.88rem;
}

.inline-loading i {
  color: var(--p-accent, #1e3a5f);
}

.token-usage-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.usage-meter {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.usage-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.82rem;
}

.usage-labels .used {
  color: var(--p-ink, #1e293b);
  font-weight: 600;
}

.usage-labels .limit {
  color: var(--p-ink-muted, #64748b);
}

.progress-bar {
  height: 10px;
  background: var(--p-border, #e2e8f0);
  border-radius: 5px;
  overflow: visible;
  position: relative;
}

.progress-fill {
  height: 100%;
  border-radius: 5px;
  transition: width 0.5s ease;
}

.progress-fill.ok {
  background: #16a34a;
}

.progress-fill.warning {
  background: #d97706;
}

.progress-fill.critical {
  background: #ea580c;
}

.progress-fill.exceeded {
  background: var(--p-danger, #ef4444);
}

.progress-overage {
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  background: repeating-linear-gradient(
    45deg,
    #dc2626,
    #dc2626 5px,
    #ef4444 5px,
    #ef4444 10px
  );
  border-radius: 0 5px 5px 0;
}

.usage-stats {
  display: flex;
  justify-content: space-between;
  font-size: 0.82rem;
}

.usage-stats .percentage {
  font-weight: 700;
}

.usage-stats .percentage.ok {
  color: #16a34a;
}

.usage-stats .percentage.warning {
  color: #d97706;
}

.usage-stats .percentage.critical {
  color: #ea580c;
}

.usage-stats .percentage.exceeded {
  color: var(--p-danger, #ef4444);
}

.usage-stats .remaining {
  color: var(--p-ink-muted, #64748b);
}

.usage-alert {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.75rem 0.9rem;
  border-radius: var(--p-radius-sm, 0.5rem);
  font-size: 0.85rem;
  line-height: 1.45;
}

.usage-alert.warning {
  background: #fffbeb;
  color: #92400e;
  border: 1px solid #fde68a;
}

.usage-alert.critical {
  background: #fff7ed;
  color: #9a3412;
  border: 1px solid #fed7aa;
}

.usage-alert.exceeded {
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.empty-state {
  text-align: center;
  padding: 1.5rem 1rem;
  color: var(--p-ink-muted, #64748b);
}

.empty-state--compact {
  padding: 0.75rem 0;
  text-align: left;
}

.empty-state i {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  display: block;
  color: var(--p-border-strong, #cbd5e1);
}

.table-wrap {
  overflow-x: auto;
}

.invoices-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}

.invoices-table th,
.invoices-table td {
  padding: 0.65rem 0.5rem;
  text-align: left;
  border-bottom: 1px solid var(--p-border, #e2e8f0);
}

.invoices-table th {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--p-ink-muted, #64748b);
}

.invoices-table td.amount {
  font-weight: 600;
  color: var(--p-ink, #1e293b);
}

.row-actions {
  display: flex;
  gap: 0.25rem;
  justify-content: flex-end;
}

.status-chip {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  text-transform: capitalize;
}

.status-chip.is-paid,
.status-chip.is-active {
  background: #dcfce7;
  color: #166534;
}

.status-chip.is-open,
.status-chip.is-draft {
  background: #f1f5f9;
  color: #64748b;
}

.status-chip.is-past_due,
.status-chip.is-uncollectible {
  background: #fef2f2;
  color: #991b1b;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: var(--p-surface-elevated, #fff);
  border: 1px solid var(--p-border, #e2e8f0);
  border-radius: var(--p-radius, 0.75rem);
  max-width: 480px;
  width: 100%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--p-border, #e2e8f0);
}

.modal-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--p-ink, #1e293b);
}

.modal-close {
  background: none;
  border: none;
  font-size: 1rem;
  color: var(--p-ink-muted, #64748b);
  cursor: pointer;
  padding: 0.25rem;
}

.modal-close:hover {
  color: var(--p-ink, #1e293b);
}

.modal-body {
  padding: 1.15rem 1.25rem;
}

.modal-body p {
  margin: 0 0 0.65rem;
  font-size: 0.9rem;
  color: var(--p-ink, #1e293b);
  line-height: 1.5;
}

.modal-footer {
  display: flex;
  gap: 0.65rem;
  justify-content: flex-end;
  padding: 0.85rem 1.25rem;
  border-top: 1px solid var(--p-border, #e2e8f0);
  background: var(--p-surface-muted, #f8fafc);
}

.text-muted {
  color: var(--p-ink-muted, #64748b);
  font-size: 0.85rem;
}

@media (max-width: 768px) {
  .card-header--split {
    flex-direction: column;
  }

  .card-actions {
    flex-direction: column;
  }

  .card-actions .btn {
    width: 100%;
    justify-content: center;
  }

  .feature-checklist {
    grid-template-columns: 1fr;
  }
}
</style>
